import React, { useEffect, useRef, useState } from 'react';
import Icon from 'components/AppIcon';
import DeleteConfirmModal from 'components/ui/DeleteConfirmModal';

const CONSENT_CONFIG = {
  granted: { bg: '#DCFCE7', color: '#15803D', label: 'Consent Granted' },
  denied: { bg: '#FEE2E2', color: '#B91C1C', label: 'Consent Denied' },
  unknown: { bg: '#F3F4F6', color: '#4B5563', label: 'Consent Unknown' },
};

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString();
}

function mapContactFromApi(contact) {
  return {
    id: contact?.id,
    name: contact?.fullName || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    source: contact?.source || 'Website',
    tags: Array.isArray(contact?.tags) ? contact.tags : [],
    whatsappConsent: contact?.whatsappConsent || 'unknown',
    convertedToDealId: contact?.convertedToDealId || null,
    createdAt: formatDate(contact?.createdAtUtc),
    timestamp: formatDate(contact?.updatedAtUtc),
  };
}

export default function ContactsTable({ searchQuery, initialContacts = [], canDelete, onEditContact, onConvertContact, onDeleteContact, onAddNew }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [contacts, setContacts] = useState([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [topScrollbarWidth, setTopScrollbarWidth] = useState(1000);
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);
  const syncSourceRef = useRef(null);

  useEffect(() => {
    setContacts((initialContacts || []).map(mapContactFromApi));
  }, [initialContacts]);

  useEffect(() => {
    const updateTopScrollbarWidth = () => {
      const measuredWidth = tableRef.current?.scrollWidth || 1000;
      setTopScrollbarWidth(measuredWidth);
    };

    updateTopScrollbarWidth();
    window.addEventListener('resize', updateTopScrollbarWidth);

    return () => {
      window.removeEventListener('resize', updateTopScrollbarWidth);
    };
  }, [contacts, searchQuery]);

  const syncScroll = (source, target) => {
    if (!source || !target) {
      return;
    }

    if (syncSourceRef.current === target) {
      return;
    }

    syncSourceRef.current = source;
    target.scrollLeft = source.scrollLeft;

    window.requestAnimationFrame(() => {
      if (syncSourceRef.current === source) {
        syncSourceRef.current = null;
      }
    });
  };

  const handleTopScroll = () => {
    syncScroll(topScrollRef.current, tableScrollRef.current);
  };

  const handleTableScroll = () => {
    syncScroll(tableScrollRef.current, topScrollRef.current);
  };

  const filtered = contacts?.filter((contact) => {
    const q = searchQuery?.toLowerCase() || '';
    return !q || [contact?.name, contact?.email, contact?.phone, contact?.source, contact?.tags?.join(' ')]?.some((f) => f?.toLowerCase()?.includes(q));
  });

  const sorted = [...(filtered || [])]?.sort((a, b) => {
    let av = a?.[sortField], bv = b?.[sortField];
    if (typeof av === 'string') av = av?.toLowerCase();
    if (typeof bv === 'string') bv = bv?.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev?.includes(id) ? prev?.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    setSelectedIds(selectedIds?.length === sorted?.length ? [] : sorted?.map((c) => c?.id));
  };

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={12}
      color={sortField === field ? 'var(--color-primary)' : 'var(--color-text-secondary)'}
    />
  );

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-border)', background: 'var(--color-card)' }}>
      {selectedIds?.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5" style={{ background: 'var(--color-primary)', borderBottom: '1px solid var(--color-border)' }}>
          <span className="font-caption text-sm font-medium text-white">{selectedIds?.length} selected</span>
          <button className="ml-auto text-white/70 hover:text-white" onClick={() => setSelectedIds([])}>
            <Icon name="X" size={14} color="currentColor" />
          </button>
        </div>
      )}

      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        className="overflow-x-auto overflow-y-hidden h-4"
        style={{ WebkitOverflowScrolling: 'touch', borderBottom: '1px solid var(--color-border)' }}
      >
        <div style={{ width: `${topScrollbarWidth}px`, height: '12px' }} />
      </div>

      <div
        ref={tableScrollRef}
        onScroll={handleTableScroll}
        className="overflow-x-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <table ref={tableRef} className="w-full" style={{ minWidth: '1000px' }}>
          <thead>
            <tr style={{ background: 'var(--color-muted)', borderBottom: '1px solid var(--color-border)' }}>
              <th className="px-4 py-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={selectedIds?.length === sorted?.length && sorted?.length > 0}
                  onChange={toggleAll}
                  className="rounded"
                  style={{ accentColor: 'var(--color-primary)' }}
                />
              </th>
              {[
                { label: 'Name', field: 'name' },
                { label: 'Email', field: 'email' },
                { label: 'Phone', field: 'phone' },
                { label: 'Source', field: 'source' },
                { label: 'Tags', field: 'tags' },
                { label: 'Consent', field: 'whatsappConsent' },
                { label: 'Status', field: 'convertedToDealId' },
                { label: 'Last Updated', field: 'timestamp' },
              ]?.map(({ label, field }) => (
                <th
                  key={field}
                  className="px-4 py-3 text-left cursor-pointer select-none"
                  onClick={() => toggleSort(field)}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                      {label}
                    </span>
                    <SortIcon field={field} />
                  </div>
                </th>
              ))}
              <th
                className="px-4 py-3 text-left sticky right-0"
                style={{ background: 'var(--color-muted)', borderLeft: '1px solid var(--color-border)', zIndex: 2 }}
              >
                <span className="font-caption text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted?.map((contact, idx) => {
              const consentCfg = CONSENT_CONFIG?.[contact?.whatsappConsent] || CONSENT_CONFIG.unknown;
              const isSelected = selectedIds?.includes(contact?.id);
              return (
                <tr
                  key={contact?.id}
                  style={{
                    background: isSelected ? 'rgba(var(--color-primary-rgb, 59,130,246), 0.05)' : idx % 2 === 0 ? 'transparent' : 'var(--color-surface-2, var(--color-muted))',
                    borderBottom: '1px solid var(--color-border)',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--color-muted)'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'var(--color-surface-2, var(--color-muted))'; }}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(contact?.id)}
                      className="rounded"
                      style={{ accentColor: 'var(--color-primary)' }}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption font-semibold text-sm" style={{ color: 'var(--color-foreground)' }}>{contact?.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{contact?.email || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{contact?.phone || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)' }}>{contact?.source}</span>
                  </td>
                  <td className="px-4 py-3">
                    {contact?.tags?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {contact.tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 rounded-full font-caption text-xs" style={{ background: '#EDE9FE', color: '#6D28D9' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: consentCfg?.bg, color: consentCfg?.color }}>
                      {consentCfg?.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {contact?.convertedToDealId ? (
                      <span className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: '#D1FAE5', color: '#065F46' }}>
                        Converted
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 rounded-full font-caption text-xs font-semibold" style={{ background: '#F3F4F6', color: '#4B5563' }}>
                        Open
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>{contact?.timestamp}</span>
                  </td>
                  <td
                    className="px-4 py-3 sticky right-0"
                    style={{ background: 'var(--color-card)', borderLeft: '1px solid var(--color-border)', zIndex: 1 }}
                  >
                    <div className="flex items-center gap-1.5">
                      <button
                        className="flex items-center justify-center rounded-md transition-colors hover:bg-muted"
                        style={{ width: 28, height: 28, color: 'var(--color-primary)' }}
                        title="View details"
                        onClick={() => window.location.assign(`/contacts/${contact?.id}`)}
                      >
                        <Icon name="Eye" size={13} color="currentColor" />
                      </button>
                      <button
                        className="flex items-center justify-center rounded-md transition-colors hover:bg-muted"
                        style={{ width: 28, height: 28, color: 'var(--color-text-secondary)' }}
                        title="Edit"
                        onClick={() => onEditContact && onEditContact(contact)}
                      >
                        <Icon name="Edit2" size={13} color="currentColor" />
                      </button>
                      {!contact?.convertedToDealId && (
                        <button
                          className="flex items-center justify-center rounded-md transition-colors hover:bg-muted"
                          style={{ width: 28, height: 28, color: '#15803D' }}
                          title="Convert to deal"
                          onClick={() => onConvertContact && onConvertContact(contact)}
                        >
                          <Icon name="TrendingUp" size={13} color="currentColor" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="flex items-center justify-center rounded-md transition-colors hover:bg-red-50"
                          style={{ width: 28, height: 28, color: '#DC2626' }}
                          title="Delete"
                          onClick={() => {
                            setDeleteTargetId(contact?.id);
                            setIsDeleteOpen(true);
                          }}
                        >
                          <Icon name="Trash2" size={13} color="currentColor" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted?.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="SearchX" size={32} color="var(--color-text-secondary)" />
                    <span className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {contacts?.length === 0 ? 'No contacts yet' : 'No contacts match your search'}
                    </span>
                    {contacts?.length === 0 && onAddNew && (
                      <button
                        onClick={onAddNew}
                        className="mt-2 px-4 py-2 rounded-lg font-caption text-sm font-medium transition-colors"
                        style={{ background: 'var(--color-primary)', color: '#fff' }}
                      >
                        Add your first contact
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid var(--color-border)' }}>
        <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          Showing {sorted?.length} of {contacts?.length} contacts
        </span>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        itemName="Contact"
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={async () => {
          if (deleteTargetId && onDeleteContact) {
            await onDeleteContact(deleteTargetId);
          }
          setContacts((prev) => prev.filter((c) => c.id !== deleteTargetId));
          setIsDeleteOpen(false);
          setDeleteTargetId(null);
        }}
      />
    </div>
  );
}